import React, { useEffect, useMemo, useState, useRef } from 'react';

import { contactApis } from '@apis/next/contactApis';
import { zipcodeCityApis } from '@apis/next/zipcodeCityApis';
import { Button } from '@components/Button';
import { FormTitle } from '@components/Forms/FormTitle';
import { QueryContainer } from '@components/QueryContainer';
import { ContactDisplayEditCard } from '@components/utilities/Contact/ContactDisplayEditCard';
import { VerticalSpace } from '@components/VerticalSpace';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { useSearch } from '@hooks/next/useSearch';
import { useCompany } from '@hooks/queries/useCompany';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useFormUtils } from '@hooks/useFormUtils';
import { CompanyDetails } from '@models/Company';
import {
  type ContactFormItem,
  type ContactType,
  type Contact,
  ContactHelper,
  ContactFormCN,
  ContactFormJP,
  BaseContactForm,
  ContactDisplayAction,
} from '@models/next/Contact';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { mapOptional } from '@utils/types';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import useEvent from 'react-use-event-hook';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    contactTitle: (category: ContactType) => `${bookingInquiryStrings[category]}情報`,
    addContact: (category: ContactType) => `+${bookingInquiryStrings[category]}を追加`,
    addPickupAddressPrompt: `該当${bookingInquiryStrings.shipper}の中国国内${bookingInquiryStrings.pickupAddress}の新規追加、もしくは編集をしてください`,
    addReceiverPrompt: `該当${bookingInquiryStrings.consignee}の日本国内${bookingInquiryStrings.receiver}の新規追加、もしくは編集をしてください`,
    confirmDeleteUser: '本当に連携をキャンセルしますか？',
    confirmDelete: (category: ContactType) =>
      `本当に${bookingInquiryStrings[category]}を削除しますか？`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  contactDisplayEditArray: {
    ...common.flexColumn,
    flex: 1,
  },
  ownedContactList: {
    ...common.flexColumn,
    rowGap: '1rem',
  },
  addOwnedContactPrompt: {
    ...common.middleTextImportant,
    ...common.textGray700,
    padding: '11px 8px',
    backgroundColor: theme.palette.base[200],
  },
}));

const SearchType = z
  .object({
    action: ContactDisplayAction,
  })
  .partial();

const ContactSingleForm: React.FC<{
  contactType: ContactType;
  item: ContactFormItem;
  ownerItem: ContactFormItem;
  ownedContactIndex?: number;
  onEdit: () => void;
  onCancel: () => void;
  onUpdated: () => void;
  onCreated?: (contact: Contact) => void;
  sameAsOwnerDisabled?: boolean;
}> = ({
  contactType,
  item,
  ownerItem,
  ownedContactIndex,
  onEdit,
  onCancel,
  onUpdated,
  onCreated,
  sameAsOwnerDisabled,
}) => {
  const form = useForm(
    (contactType === 'shipper' || contactType === 'pickup_address'
      ? ContactFormCN
      : ContactFormJP) as typeof BaseContactForm,
    {
      initialOutput: item.action === 'create' ? empty : item.initialOutput,
      enableReinitialize: true,
    },
  );

  const postContact = contactApis.usePost('/contact');

  const patchContact = contactApis.usePatch('/contact/:id', {
    params: {
      id: item.id,
    },
  });

  const deleteContact = contactApis.useDelete('/contact/:id', {
    params: {
      id: item.id,
    },
  });

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [postContact, patchContact, deleteContact],
  });

  const { confirm, renderDialog } = useConfirmDialog();

  const history = useHistory();

  return (
    <form
      {...form.bindForm({
        async onSubmit(values) {
          if (item.action === 'edit') {
            await patchContact.mutateAsync({
              ...values,
              social_credit_id: values.social_credit_id ?? '',
              email: values.email ?? '',
            });
            onUpdated();
          } else {
            const contact = await postContact.mutateAsync({
              ...values,
              social_credit_id: values.social_credit_id ?? '',
              owner: ownerItem.id,
              email: values.email ?? '',
              contact_type: contactType,
            });
            onCreated?.(contact);
          }
        },
      })}
    >
      {renderAlertsAndSpinnerModal()}
      {renderDialog()}

      <ContactDisplayEditCard
        ownerAction="edit"
        item={item}
        field={form.root}
        type={contactType}
        onEdit={onEdit}
        onCancel={() => {
          if (item.action === 'edit') {
            form.root.setOutput(item.initialOutput);
          }
          onCancel();
        }}
        onDelete={async () => {
          if (
            (await confirm({
              message: strings.confirmDelete(contactType),
            })) === 'confirmed'
          ) {
            await deleteContact.mutateAsync(undefined);

            if (contactType === 'shipper' || contactType === 'consignee') {
              history.replace(
                contactType === 'shipper'
                  ? url('/utilities/Contact/Shippers/Shippers')
                  : url('/utilities/Contact/Consignees/Consignees'),
              );
            } else {
              onUpdated();
            }
          }
        }}
        onChangeSameAsOwner={async (sameAsOwner) => {
          if (ownerItem.action === 'edit') {
            if (sameAsOwner) {
              if (item.action === 'create') {
                const contact = await postContact.mutateAsync({
                  ...ownerItem.initialOutput,
                  social_credit_id: ownerItem.initialOutput.social_credit_id ?? '',
                  email: ownerItem.initialOutput.email ?? '',
                  owner: ownerItem.id,
                  contact_type: contactType,
                });
                onCreated?.(contact);
              } else {
                await patchContact.mutateAsync({
                  ...ownerItem.initialOutput,
                  social_credit_id: ownerItem.initialOutput.social_credit_id ?? '',
                  email: ownerItem.initialOutput.email ?? '',
                  contact_type: contactType,
                });
                onUpdated();
              }
            } else {
              onEdit();
            }
          }
        }}
        ownedContactIndex={ownedContactIndex}
        sameAsOwnerDisabled={sameAsOwnerDisabled}
      />
    </form>
  );
};

export const ContactDisplayEditArrayContent: React.FC<
  ContactDisplayEditArrayProps & {
    ownerItem: ContactFormItem;
    items: ContactFormItem[];
    onEditOwner: () => void;
    onCancelOwner: () => void;
    onEdit: (id: number) => void;
    /**
     *
     * @param id
     * @returns cancel editing, or delete the current creating client side item.
     */
    onCancel: (id: number) => void;
    onUpdated: (id?: number) => void;
    onCreated: (id: number, contact: Contact) => void;
    onAdd: () => void;
    sameAsOwnerDisabled?: boolean;
  }
> = ({
  contactType,
  ownedContactType,
  items,
  ownerItem,
  onEditOwner,
  onCancelOwner,
  onEdit,
  onCancel,
  onUpdated,
  onCreated,
  onAdd,
  sameAsOwnerDisabled,
}) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search } = useSearch(SearchType);

  const ownedContactListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (search.action === 'createOwnedContact') {
      ownedContactListRef.current?.scrollIntoView({
        block: 'end',
      });
    }
  }, []);

  const { confirm, renderDialog } = useConfirmDialog();

  const history = useHistory();

  const deleteContact = contactApis.useDelete('/contact/:id', {
    params: {
      id: ownerItem.id,
    },
  });

  const { renderAlertsAndSpinnerModal } = useFormUtils({ mutations: [deleteContact] });

  return (
    <div className={classes.contactDisplayEditArray}>
      {renderDialog()}
      {renderAlertsAndSpinnerModal()}

      <FormTitle title={strings.contactTitle(contactType)} />
      <VerticalSpace height={8} />

      <ContactSingleForm
        contactType={contactType}
        ownerItem={ownerItem}
        item={ownerItem}
        onCancel={onCancelOwner}
        onEdit={onEditOwner}
        onUpdated={onUpdated}
      />

      <VerticalSpace height={32} />
      <FormTitle title={strings.contactTitle(ownedContactType)} />
      <VerticalSpace height={8} />

      {!(ownerItem.action === 'edit' && ownerItem.displayOnly) && (
        <div className={classes.addOwnedContactPrompt}>
          {contactType === 'shipper' ? strings.addPickupAddressPrompt : strings.addReceiverPrompt}
        </div>
      )}

      <VerticalSpace height={16} />

      <div className={classes.ownedContactList} ref={ownedContactListRef}>
        {items.map((item, i) => (
          <ContactSingleForm
            key={item.id}
            contactType={ownedContactType}
            ownerItem={ownerItem}
            item={item}
            onCancel={() => onCancel(item.id)}
            onEdit={() => onEdit(item.id)}
            onUpdated={() => onUpdated(item.id)}
            onCreated={(contact) => onCreated(item.id, contact)}
            ownedContactIndex={i}
            sameAsOwnerDisabled={sameAsOwnerDisabled}
          />
        ))}
      </div>

      {items.length > 0 && <VerticalSpace height={32} />}

      {!(ownerItem.action === 'edit' && ownerItem.displayOnly) && (
        <span className={clsx(classes.link, classes.baseButton, classes.middleText)}>
          <span onClick={onAdd}>{strings.addContact(ownedContactType)}</span>
        </span>
      )}

      <VerticalSpace height={16} />

      <Button
        color="grey"
        size="small"
        text={formStrings.delete}
        disabled={ownerItem.action === 'edit' && ownerItem.displayOnly}
        style={{ width: 100 }}
        type="button"
        onClick={async () => {
          if ((await confirm({ message: strings.confirmDelete(contactType) })) === 'confirmed') {
            await deleteContact.mutateAsync(undefined);

            history.replace(
              contactType === 'shipper'
                ? url('/utilities/Contact/Shippers/Shippers')
                : url('/utilities/Contact/Consignees/Consignees'),
            );
          }
        }}
      />

      <VerticalSpace height={16} />
    </div>
  );
};

export interface ContactDisplayEditArrayProps {
  contactId: number;
  contactType: ContactType;
  ownedContactType: ContactType;
}

export const ContactDisplayEditArray: React.FC<ContactDisplayEditArrayProps> = React.memo(
  (props) => {
    const { contactId, contactType } = props;

    const companyQuery = useCompany();

    const { search, updateSearch } = useSearch(SearchType);

    const contactQuery = contactApis.useGet(
      '/contact/:id',
      {
        params: {
          id: contactId,
        },
      },
      {
        staleTime: Infinity,
      },
    );

    const ownedContactsQuery = contactApis.useGet(
      '/contact',
      {
        queries: {
          owner_id: contactId,
          ordering: ['created_at'],
        },
      },
      {
        staleTime: Infinity,
      },
    );

    const cityByZipcodeQuery = zipcodeCityApis.useGet(
      '/zipcode-city/city/:zipcode',
      {
        params: {
          zipcode: contactQuery.data?.zip_code!,
        },
      },
      {
        enabled: !!contactQuery.data?.zip_code && contactType === 'shipper',
        keepPreviousData: true,
      },
    );

    const validCNZipcode = useMemo(
      () => cityByZipcodeQuery.data?.length || 0 >= 1,
      [cityByZipcodeQuery.data],
    );

    const mapContactToFormItem = (
      ownerContact: Contact,
      contact: Contact,
      company: CompanyDetails,
    ): ContactFormItem => ({
      action: 'edit',
      id: contact.id,
      initialOutput: ContactHelper.mapToForm(contact),
      displayOnly: ownerContact.company_id != null && ownerContact.company_id !== company.id,
      mode:
        ownerContact !== contact && ContactHelper.areEqual(contact, ownerContact)
          ? 'sameAsOwner'
          : 'display',
    });

    const initialItems = useMemo((): ContactFormItem[] => {
      if (contactQuery.data && ownedContactsQuery.data && companyQuery.data) {
        return [
          ...ownedContactsQuery.data.map((contact) =>
            mapContactToFormItem(contactQuery.data!, contact, companyQuery.data),
          ),
          ...(search.action === 'createOwnedContact'
            ? [
                {
                  action: 'create',
                  id: Date.now(),
                  mode: 'edit',
                } as const,
              ]
            : []),
        ];
      } else {
        return [];
      }
    }, [contactQuery.data, ownedContactsQuery.data, companyQuery.data]);

    const initialOwnerItem = useMemo((): ContactFormItem | undefined => {
      if (contactQuery.data && companyQuery.data) {
        return mapContactToFormItem(contactQuery.data, contactQuery.data, companyQuery.data);
      } else {
        return undefined;
      }
    }, [contactQuery.data, companyQuery.data]);

    // `_items` stores the local data, which is undefined when not modified.
    // this avoids setState upon `initialItems` is ready.
    const [_items, setItems] = useState<ContactFormItem[] | undefined>(undefined);
    const items = _items ?? initialItems;

    const [_ownerItem, setOwnerItem] = useState<ContactFormItem | undefined>(undefined);
    const ownerItem = _ownerItem ?? initialOwnerItem;

    const reconcileItems = useEvent(
      (updatedId: number | undefined, contact: Contact, ownedContacts: Contact[]) => {
        setOwnerItem(mapContactToFormItem(contact, contact, companyQuery.data!));

        setItems(
          items.flatMap((item) =>
            item.action === 'create'
              ? [item]
              : item.mode === 'display' || item.mode === 'sameAsOwner' || item.id === updatedId
              ? mapOptional(
                  ownedContacts.find((c) => c.id === item.id),
                  (c) => mapContactToFormItem(contact, c, companyQuery.data!),
                ) ?? []
              : [item],
          ),
        );
      },
    );

    return (
      <QueryContainer queries={[contactQuery, ownedContactsQuery, companyQuery]}>
        <ContactDisplayEditArrayContent
          {...props}
          items={items}
          ownerItem={ownerItem!}
          onEditOwner={() =>
            setOwnerItem(ownerItem?.action === 'edit' ? { ...ownerItem, mode: 'edit' } : ownerItem)
          }
          onCancelOwner={() =>
            setOwnerItem(
              ownerItem?.action === 'edit' ? { ...ownerItem, mode: 'display' } : ownerItem,
            )
          }
          onEdit={(id) =>
            setItems(items.map((item) => (item.id === id ? { ...item, mode: 'edit' } : item)))
          }
          onCancel={(id) =>
            setItems(
              items.flatMap((item) =>
                item.id === id
                  ? item.action === 'edit'
                    ? {
                        ...item,
                        mode: 'display',
                      }
                    : []
                  : item,
              ),
            )
          }
          onUpdated={async (id) => {
            const [contact, ownedContacts] = await Promise.all([
              contactQuery.refetch(),
              ownedContactsQuery.refetch(),
            ]);
            if (contact.data && ownedContacts.data) {
              reconcileItems(id, contact.data, ownedContacts.data);
            }
            updateSearch({ action: 'editSuccess' });
          }}
          onCreated={(id, contact) => {
            setItems(
              items.map((item) =>
                item.id === id
                  ? mapContactToFormItem(contactQuery.data!, contact, companyQuery.data!)
                  : item,
              ),
            );
            updateSearch({ action: 'editSuccess' });
          }}
          onAdd={() =>
            setItems([
              ...items,
              {
                action: 'create',
                id: Date.now(),
                mode: 'edit',
              },
            ])
          }
          sameAsOwnerDisabled={contactType === 'shipper' && !validCNZipcode}
        />
      </QueryContainer>
    );
  },
);
